// @flow

import React from 'react';
import { withTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import FormGroup from '@material-ui/core/FormGroup';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { logGAEvent } from '../util/log-ga';
import { addBodyModalClass, removeBodyModalClass } from '../util/modal';
import { Checkbox, TextField } from '@material-ui/core';
import { validateEmail } from '../util/validate-email';
import FormHelperText from '@material-ui/core/FormHelperText';
import { validatePhone } from '../util/validate-phone';
import { UiPhoneTextField } from '../util/ui-phone-text-field';

type Props = {
  applyCallback: Function,
  t: (value: string, opts?: any) => string,
};

type State = {
  modalOpen: boolean;
  firstName: ?string,
  lastName: ?string,
  email: ?string,
  phoneNumber: ?string,
  textFollowups: ?Boolean,
  errors: Object,
}

const formEntryStyle = {
  paddingLeft: '20px',
  paddingRight: '20px',
  maxWidth: '400px',
  width: '100%',
  marginBottom: '10px',
};

const formStyle = {
  height: '500px',
  maxWidth: '400px',
};

class Transition extends React.PureComponent<Props>{
  render() {
    // $flow-disable-next-line
    return <Slide direction="up" {...this.props} />;
  }
}

class ManualApply extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      modalOpen: false,
      firstName: null,
      lastName: null,
      email: null,
      phoneNumber: null,
      textFollowups: null,
      errors: {},
    };
  }

  onChange = (field: string) => (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      [field]: event.currentTarget.value,
      errors: {
        ...this.state.errors,
        [field]: undefined,
      }
    });
  };

  onPhoneChange = (phoneNumber: string | null) => {
    this.setState({
      phoneNumber,
      errors: {
        ...this.state.errors,
        phoneNumber: undefined,
      }
    });
  }

  onCheckboxChange = (field: string) => (_event: any, checked: boolean) => {
    this.setState({
      [field]: checked,
      errors: {
        ...this.state.errors,
        [field]: undefined,
      }
    });
  };

  apply = () => {
    const { t } = this.props;
    const errors = {};
    if (!this.state.firstName || this.state.firstName.trim().length === 0) {
      errors['firstName'] = t('required');
    }

    if (!this.state.lastName || this.state.lastName.trim().length === 0) {
      errors['lastName'] = t('required');
    }

    const enteredStringEmail = String(this.state.email || '').trim();
    const enteredStringPhone = String(this.state.phoneNumber || '').trim();

    if (enteredStringEmail.length === 0 && enteredStringPhone.length === 0) {
      errors['email'] = t('oneRequired');
      errors['phoneNumber'] = t('oneRequired');
    }

    const emailError = validateEmail(enteredStringEmail);
    if (emailError) {
      errors['email'] = emailError;
    }

    const phoneError = validatePhone(enteredStringPhone);
    if (phoneError) {
      errors['phoneNumber'] = phoneError;
    }

    this.setState({
      errors,
    });
    if (Object.keys(errors).length > 0) {
      return;
    }

    const valuesToSubmit = Object.assign({}, {
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      email: enteredStringEmail,
      phoneNumber: enteredStringPhone,
      textFollowups: this.state.textFollowups,
    });
    Object.keys(valuesToSubmit).forEach((key) => {
      const val = valuesToSubmit[key];
      if (val && typeof val === 'string') {
        valuesToSubmit[key] = val.trim();
      } else if (val) {
        valuesToSubmit[key] = val;
      } else {
        valuesToSubmit[key] = null;
      }
    });
    valuesToSubmit.type = 'manual';
    this.props.applyCallback(valuesToSubmit);
    this.handleClose();
  };

  openApplyDialog = () => {
    addBodyModalClass();
    this.setState({
      modalOpen: true,
    });
    logGAEvent('Application', 'Clicked Manual Apply');
  }

  handleClose = () => {
    removeBodyModalClass();
    this.setState({
      modalOpen: false,
    });
  }

  render() {
    const { t } = this.props;
    return (
      <div style={{ textAlign: 'center' }}>
        <Button variant="outlined" color="default" style={{ paddingTop: '10.5px', paddingBottom: '10.5px' }} onClick={this.openApplyDialog}>
          {t('applyManually')}
        </Button>
        <Dialog
          open={this.state.modalOpen}
          onClose={this.handleClose}
          fullScreen
          TransitionComponent={Transition}
        >
          <div style={formStyle}>
            <DialogTitle id="form-dialog-title">{t('basicInformation')}</DialogTitle>
            <DialogContent>

              <FormGroup>
                <FormControl style={formEntryStyle}>
                  <TextField
                    name="firstName"
                    label={t('firstName')}
                    margin="dense"
                    onChange={this.onChange('firstName')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={this.state.errors.firstName !== undefined}
                  />
                  {this.state.errors.firstName &&
                  <FormHelperText error>
                    {this.state.errors.firstName}
                  </FormHelperText>
                  }
                </FormControl>
                <FormControl style={formEntryStyle}>
                  <TextField
                    name="lastName"
                    label={t('lastName')}
                    margin="dense"
                    onChange={this.onChange('lastName')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={this.state.errors.lastName !== undefined}
                  />
                  {this.state.errors.lastName &&
                  <FormHelperText error>
                    {this.state.errors.lastName}
                  </FormHelperText>
                  }
                </FormControl>
                <FormControl style={formEntryStyle}>
                  <TextField
                    name="email"
                    label={t('email')}
                    margin="dense"
                    onChange={this.onChange('email')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={this.state.errors.email !== undefined}
                  />
                  {this.state.errors.email &&
                  <FormHelperText error>
                    {this.state.errors.email}
                  </FormHelperText>
                  }
                </FormControl>
                <FormControl style={formEntryStyle}>
                  <UiPhoneTextField
                    name="phoneNumber"
                    value={this.state.phoneNumber}
                    label={t('phoneNumber')}
                    margin="dense"
                    onChange={this.onPhoneChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={this.state.errors.phoneNumber !== undefined}
                  />
                  {this.state.errors.phoneNumber &&
                  <FormHelperText error>
                    {this.state.errors.phoneNumber}
                  </FormHelperText>
                  }
                </FormControl>
                <FormControl style={formEntryStyle}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="textFollowups"
                      label={t('mayWeTextYou')}
                      margin="dense"
                      onChange={this.onCheckboxChange('textFollowups')}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={this.state.errors.textFollowups !== undefined}
                      />
                    }
                    label={t('mayWeTextYou')}
                  />
                </FormControl>
              </FormGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleClose} color="default">
                {t('cancel')}
              </Button>
              <Button variant="contained" color="secondary" onClick={this.apply}>
                {t('apply')}
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      </div>
    );
  }
}

export default withTranslation()(ManualApply);
